import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Plus, Route as RouteIcon, AlertTriangle, RotateCw, Loader2, Building2, Stethoscope, UserRound } from 'lucide-react';
import ProgrammeTemplateCard from '../components/programmes/ProgrammeTemplateCard';
import ProgrammeEditor from '../components/programmes/ProgrammeEditor';
import useProgrammeActions from '../components/programmes/useProgrammeActions';
import ProgrammesTab from '../components/doctorTabs/ProgrammesTab';
import SearchableSelect from '../components/common/SearchableSelect';
import { fetchProgrammes } from '../redux/programmeSlice';
import { getDoctorNameList } from '../redux/doctorSlice';
import { groupByMeantFor } from '../utils/careProgrammes';
import { serverReason } from '../components/patientTabs/prescriptionHelpers';

// ADMIN → HOSPITAL PROGRAMMES (care programmes, Round B). The hospital's own
// programmes, which every doctor of the hospital may start (or copy to make
// their own). Built by the admin, or by staff whose role has `checks.manage`.
// Every programme is the hospital's own definition — nothing is pre-made or
// speciality-specific; an optional free "Meant for" label only groups the
// list (owner, 26 Sep 2026).
//
// The ADMIN also manages any doctor's OWN programmes (parity with check
// sets): "Doctors' programmes" → pick a doctor → that doctor's Settings →
// Programmes screen, acting on that doctor. Staff with checks.manage get the
// hospital's programmes only (the server refuses them a doctor's with
// STAFF_TENANT_PROGRAMMES_ONLY), so the choice is not drawn for them.
//
// The route is wrapped in TenantFeatureRoute("care_programmes") and
// PermissionGuard("checks.manage"); the server enforces both.

const SCOPES = [
    { key: 'hospital', icon: Building2, labelKey: 'scope_hospital' },
    { key: 'doctor', icon: Stethoscope, labelKey: 'scope_doctors' },
];

function HospitalProgrammes() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const isAdmin = (typeof localStorage !== 'undefined' && localStorage.getItem('Role')) === 'admin';
    const { manage, busyId } = useSelector((state) => state.programmes);
    const { doctorNameList, loadingList } = useSelector((state) => state.doctor);
    const { remove } = useProgrammeActions();
    const [editor, setEditor] = useState({ open: false, programme: null });
    const [scope, setScope] = useState('hospital');
    const [doctorId, setDoctorId] = useState('');
    const [askedDoctors, setAskedDoctors] = useState(false);
    const doctorScope = isAdmin && scope === 'doctor';

    // The hospital's list; a doctor's list is ProgrammesTab's to ask for.
    useEffect(() => {
        if (!doctorScope) dispatch(fetchProgrammes({}));
    }, [dispatch, doctorScope]);

    const chooseScope = (key) => {
        if (!isAdmin) return;
        setScope(key);
        if (key === 'doctor' && scope !== 'doctor') {
            setAskedDoctors(true);
            dispatch(getDoctorNameList());
        }
    };

    const doctors = Array.isArray(doctorNameList) ? doctorNameList : [];
    const picked = doctors.find((d) => String(d.doctorId) === String(doctorId)) || null;
    const doctorOptions = doctors.map((d) => ({ value: d.doctorId, label: d.name || t('common.doctor_label', 'Doctor') }));

    // The store holds one list at a time; the hospital's is the one asked
    // without a doctor.
    const current = manage.doctorId === null;
    const loaded = current && manage.loaded;
    const loadError = current ? manage.error : null;
    const groups = groupByMeantFor(manage.hospital);

    return (
        <div data-testid="hospital-programmes-page" className="mx-auto rounded-2xl border border-gray-100 bg-white p-4 shadow-xl sm:p-6">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h1 className="flex items-center gap-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-2xl font-bold text-transparent sm:text-3xl">
                        <RouteIcon className="h-7 w-7 text-[#4ABDBD]" aria-hidden="true" />
                        {t('programmes.hospital_page_title')}
                    </h1>
                    <p className="mt-1 max-w-3xl text-sm text-gray-500">
                        {doctorScope ? t('programmes.doctors_scope_subheading') : t('programmes.hospital_page_subheading')}
                    </p>
                </div>
                {!doctorScope && (
                    <button
                        type="button"
                        data-testid="hospital-programmes-new"
                        onClick={() => setEditor({ open: true, programme: null })}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-4 py-2 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl"
                    >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                        {t('programmes.new_programme')}
                    </button>
                )}
            </div>

            {isAdmin && (
                <div
                    role="tablist"
                    aria-label={t('programmes.scope_label')}
                    data-testid="hospital-programmes-scope"
                    className="mb-6 flex w-full flex-wrap gap-1 rounded-2xl border border-gray-200 bg-gray-50 p-1 sm:w-fit"
                >
                    {SCOPES.map(({ key, icon, labelKey }) => {
                        const Icon = icon;
                        const on = scope === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={on}
                                data-testid={`hospital-programmes-scope-${key}`}
                                onClick={() => chooseScope(key)}
                                className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold transition-colors sm:flex-none ${on
                                    ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white shadow'
                                    : 'text-gray-600 hover:bg-white hover:text-[#2D8A8A]'}`}
                            >
                                <Icon className="h-4 w-4" aria-hidden="true" />
                                {t(`programmes.${labelKey}`)}
                            </button>
                        );
                    })}
                </div>
            )}

            {doctorScope && (
                <div data-testid="hospital-programmes-doctor-scope" className="space-y-6">
                    <div className="flex flex-col gap-1.5 sm:max-w-sm">
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t('programmes.doctor_picker_label')}</span>
                        <SearchableSelect
                            testId="hospital-programmes-doctor"
                            value={doctorId}
                            onChange={(v) => setDoctorId(v || '')}
                            options={doctorOptions}
                            placeholder={loadingList ? t('programmes.loading') : t('programmes.pick_doctor')}
                            searchPlaceholder={t('programmes.search_doctor')}
                            icon={UserRound}
                            disabled={loadingList}
                            variant="form"
                        />
                    </div>
                    {picked ? (
                        <ProgrammesTab key={picked.doctorId} doctor={{ doctorId: picked.doctorId, name: picked.name }} />
                    ) : (
                        <div data-testid="hospital-programmes-pick-doctor" className="mx-auto max-w-xl rounded-2xl border border-dashed border-gray-200 p-10 text-center">
                            <Stethoscope className="mx-auto mb-3 h-10 w-10 text-[#9DB3B1]" aria-hidden="true" />
                            <p className="text-sm text-gray-500">
                                {!loadingList && askedDoctors && doctors.length === 0 ? t('programmes.no_doctors') : t('programmes.pick_doctor_hint')}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {!doctorScope && manage.loading && !loaded && (
                <div data-testid="hospital-programmes-loading" className="flex items-center justify-center gap-2 py-10 text-sm text-[#4ABDBD]">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    {t('programmes.loading')}
                </div>
            )}

            {!doctorScope && loadError && (
                <div role="alert" data-testid="hospital-programmes-error" className="mx-auto flex max-w-xl flex-col items-center gap-2 rounded-2xl border border-red-100 bg-red-50 p-6 text-center">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-red-700">
                        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                        {serverReason(loadError, t('programmes.load_failed'), i18n.language)}
                    </p>
                    <button
                        type="button"
                        data-testid="hospital-programmes-retry"
                        onClick={() => dispatch(fetchProgrammes({}))}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                    >
                        <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('programmes.retry')}
                    </button>
                </div>
            )}

            {!doctorScope && loaded && !loadError && (manage.hospital.length === 0 ? (
                <div data-testid="hospital-programmes-empty" className="mx-auto max-w-xl rounded-2xl border border-dashed border-gray-200 p-10 text-center">
                    <RouteIcon className="mx-auto mb-3 h-10 w-10 text-[#9DB3B1]" aria-hidden="true" />
                    <p className="text-sm text-gray-500">{t('programmes.no_hospital_programmes_admin')}</p>
                </div>
            ) : (
                <div className="space-y-6" data-testid="hospital-programmes-list">
                    {groups.map((g) => (
                        <section key={g.label || '_general'} data-testid={`hospital-programmes-group-${g.label || 'general'}`}>
                            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#2D8A8A]">{g.label || t('programmes.general_group')}</h2>
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                                {g.programmes.map((p) => (
                                    <ProgrammeTemplateCard
                                        key={p.programmeId}
                                        programme={p}
                                        busy={busyId === p.programmeId}
                                        onEdit={(x) => setEditor({ open: true, programme: x })}
                                        onDelete={remove}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            ))}

            <ProgrammeEditor
                isOpen={editor.open && !doctorScope}
                ownerType="tenant"
                programme={editor.programme}
                onClose={() => setEditor({ open: false, programme: null })}
            />
        </div>
    );
}

export default HospitalProgrammes;
